import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Send } from 'lucide-react';

const ROLE_LABEL = { manager: 'roleManager', staff: 'roleStaff', tenant: 'roleTenant', customer: 'roleCustomer', sales: 'roleSales' };
const ROLE_COLOR = { manager: 'text-sky-300', staff: 'text-emerald-300', tenant: 'text-amber-300', customer: 'text-amber-300', sales: 'text-emerald-300' };

// Locale-aware compact timestamp: time if today, else "MMM d, time".
const fmtTs = (iso) => {
    try {
        const d = new Date(iso);
        const sameDay = d.toDateString() === new Date().toDateString();
        return sameDay
            ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

// Reusable operational request message thread. Auth is delegated: the parent
// passes `list()` and `send(text)` so this works from Command Center (bearer),
// Staff Hub (staff session header) and the tenant card view (no auth) alike.
// NO read receipts by design.
export function RequestThread({ list, send, myRole, autoFocus = false }) {
    const { t } = useTranslation();
    const [msgs, setMsgs] = useState(null);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const endRef = useRef(null);

    const refresh = useCallback(() => {
        list().then((data) => setMsgs(data.messages || [])).catch(() => setMsgs([]));
    }, [list]);

    useEffect(() => {
        refresh();
        // Near-real-time: re-fetch every 5s so new manager/staff/tenant messages
        // appear without a manual refresh. No spinner (msgs stays populated).
        const iv = setInterval(refresh, 5000);
        return () => clearInterval(iv);
    }, [refresh]);
    useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [msgs]);

    const submit = async (e) => {
        e.preventDefault();
        const body = text.trim();
        if (!body || sending) return;
        setSending(true);
        try {
            await send(body);
            setText('');
            await refresh();
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="flex flex-col" data-testid="request-thread">
            <div className="max-h-52 overflow-y-auto space-y-2 pr-1 mb-2" data-testid="request-thread-list">
                {msgs === null ? (
                    <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-500" /></div>
                ) : msgs.length === 0 ? (
                    <p className="text-xs text-slate-500 py-4 text-center" data-testid="request-thread-empty">{t('commandCenter.comm.noMessages')}</p>
                ) : msgs.map((m) => {
                    const mine = m.sender_role === myRole;
                    return (
                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`request-msg-${m.id}`}>
                            <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? 'bg-sky-600/25 border border-sky-500/30' : 'bg-slate-800/70 border border-slate-700'}`}>
                                <p className={`text-[10px] font-semibold mb-0.5 ${ROLE_COLOR[m.sender_role] || 'text-slate-400'}`}>
                                    {m.sender_name} · {t(`commandCenter.comm.${ROLE_LABEL[m.sender_role] || 'roleStaff'}`)}
                                    {m.created_at && <span className="ml-1.5 font-normal text-slate-400" data-testid={`request-msg-ts-${m.id}`}>· {fmtTs(m.created_at)}</span>}
                                </p>
                                <p className="text-sm text-slate-100 whitespace-pre-wrap break-words">{m.body}</p>
                            </div>
                        </div>
                    );
                })}
                <div ref={endRef} />
            </div>
            <form onSubmit={submit} className="flex items-center gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)} autoFocus={autoFocus}
                    data-testid="request-thread-input" placeholder={t('commandCenter.comm.messagePlaceholder')}
                    className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500" />
                <button type="submit" disabled={sending || !text.trim()} data-testid="request-thread-send"
                    className="shrink-0 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white rounded-lg px-3 py-2 flex items-center gap-1.5 text-sm">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
            </form>
        </div>
    );
}
